// frontend/src/components/client/mobile/MobileInvoices.jsx
import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, CreditCard, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { getInvoices, getInvoiceStats } from '../../../services/clientApi';

const STATUS = {
  pending: { label: 'En attente', bg: 'rgba(234,179,8,0.15)',  color: '#eab308', Icon: Clock },
  paid:    { label: 'Payée',      bg: 'rgba(34,197,94,0.15)',  color: '#22c55e', Icon: CheckCircle },
  overdue: { label: 'En retard',  bg: 'rgba(239,68,68,0.15)',  color: '#ef4444', Icon: AlertCircle },
};

const Badge = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: s.bg, color: s.color }}>
      <s.Icon size={10} />
      {s.label}
    </span>
  );
};

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR') : '—';

const FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'pending', label: 'En attente' },
  { id: 'paid', label: 'Payées' },
];

const MobileInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const [invRes, statsRes] = await Promise.all([
        getInvoices(filter === 'all' ? null : filter),
        getInvoiceStats(),
      ]);
      setInvoices(invRes.data.invoices || []);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ padding: '0 16px 100px' }}>

      {/* Stats summary */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          <div style={{ background: '#1e293b', borderRadius: 16, border: '0.5px solid #334155', padding: '14px' }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>En attente</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#eab308' }}>{fmt(stats.pending?.total)}</div>
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{stats.pending?.count || 0} facture(s)</div>
          </div>
          <div style={{ background: '#1e293b', borderRadius: 16, border: '0.5px solid #334155', padding: '14px' }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Payées</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#22c55e' }}>{fmt(stats.paid?.total)}</div>
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{stats.paid?.count || 0} facture(s)</div>
          </div>
        </div>
      )}

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              flex: 1, padding: '9px 4px', borderRadius: 12, border: 'none',
              background: filter === f.id ? 'rgba(59,130,246,0.18)' : '#1e293b',
              color: filter === f.id ? '#3b82f6' : '#64748b',
              fontSize: 12, fontWeight: filter === f.id ? 600 : 400,
              cursor: 'pointer',
              outline: filter === f.id ? '1px solid rgba(59,130,246,0.35)' : '0.5px solid #334155',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      ) : invoices.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
          <FileText size={40} color="#334155" style={{ marginBottom: 12 }} />
          <p style={{ color: '#475569' }}>Aucune facture trouvée</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {invoices.map((inv) => (
            <div key={inv.id} style={{ background: '#1e293b', borderRadius: 18, border: '0.5px solid #334155', overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FileText size={18} color="#3b82f6" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>#{inv.invoice_number}</span>
                      <Badge status={inv.status} />
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{fmt(inv.amount)}</div>
                    <div style={{ fontSize: 11, color: '#475569' }}>
                      Émise le {fmtDate(inv.created_at)}
                      {inv.due_date && ` · Échéance ${fmtDate(inv.due_date)}`}
                    </div>
                  </div>
                </div>
              </div>
              {/* Action row */}
              <div style={{ display: 'flex', borderTop: '0.5px solid #334155' }}>
                <button style={{ flex: 1, padding: '12px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#64748b', fontSize: 12 }}>
                  <Eye size={15} /> Voir
                </button>
                <div style={{ width: '0.5px', background: '#334155' }} />
                <button style={{ flex: 1, padding: '12px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#64748b', fontSize: 12 }}>
                  <Download size={15} /> Télécharger
                </button>
                {inv.status === 'pending' && (
                  <>
                    <div style={{ width: '0.5px', background: '#334155' }} />
                    <button style={{ flex: 1.2, padding: '12px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#22c55e', fontSize: 12, fontWeight: 600 }}>
                      <CreditCard size={15} /> Payer
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileInvoices;